import { useState } from 'react';
import { useApi } from '@glyph/core';
import { Button, Logo, TextInput, useCapsLock } from '@glyph/ui';

/** Sign in: one centred card. The authenticator field appears only when the account has two-factor enabled. */
export function SignInPage({ onSignedIn }: { onSignedIn: () => void }) {
  const caps = useCapsLock();
  const api = useApi();
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [code, setCode] = useState('');
  const [needCode, setNeedCode] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      await api.login(email, pw, code.replace(/\s/g, ''));
      onSignedIn();
    } catch (x) {
      const msg = (x as Error).message || 'Sign in failed';
      if (/authenticator code required/i.test(msg)) setNeedCode(true);
      else setErr(msg);
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="flex min-h-screen items-center justify-center bg-bg px-4 py-10">
      <form onSubmit={(e) => void submit(e)} className="flex w-full max-w-[380px] flex-col gap-6 rounded-2xl border border-line bg-panel p-7 md:rounded-xl">
        <Logo sub="Control" />
        <h1 className="m-0 text-2xl font-semibold leading-none">Sign in</h1>
        <label className="flex flex-col gap-2">
          <span className="micro text-fg3">Email or username</span>
          <TextInput autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required block autoFocus />
        </label>
        <label className="flex flex-col gap-2">
          <span className="micro text-fg3">Password</span>
          <TextInput type="password" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} required block {...caps.handlers} />
          {caps.on && (
            <span role="status" className="font-mono text-xs font-medium text-warn">
              CAPS LOCK IS ON · passwords are case-sensitive
            </span>
          )}
        </label>
        {needCode && (
          <label className="flex flex-col gap-2">
            <span className="micro text-fg3">Authenticator code</span>
            <TextInput variant="mono" inputMode="numeric" autoComplete="one-time-code" placeholder="000000" value={code} onChange={(e) => setCode(e.target.value)} required block autoFocus />
          </label>
        )}
        {err && <span className="text-sm text-warn">{err}</span>}
        <Button type="submit" variant="primary" size="lg" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </main>
  );
}
